import { NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { VirtualScrollerModule } from '@iharbeck/ngx-virtual-scroller';
import { Subject } from 'rxjs';
import { debounceTime, startWith, takeUntil } from 'rxjs/operators';

import { trackByProp } from '@recruitee/common';
import { User } from '@recruitee/http/types';
import { TranslatePipe } from '@recruitee/i18n';

import { DialogBodyComponent } from '../../dialog/dialog-body/dialog-body.component';
import { DialogFooterComponent } from '../../dialog/dialog-footer/dialog-footer.component';
import { DialogHeaderComponent } from '../../dialog/dialog-header/dialog-header.component';
import { CheckboxComponent } from '../../forms/checkbox/checkbox.component';
import { FormFieldComponent } from '../../forms/form-field/form-field.component';
import { InputComponent } from '../../forms/input/input.component';
import { AvatarComponent } from '../avatar/avatar.component';
import { BoxComponent } from '../box/box.component';
import { ButtonComponent } from '../button/components/button/button.component';
import { ButtonLoadingDirective } from '../button/directives/button-loading/button-loading.directive';
import { DividerComponent } from '../divider/divider.component';
import { EmptyStateComponent } from '../empty-state/empty-state.component';
import { GroupComponent } from '../group/group.component';
import { HeadingComponent } from '../heading/heading.component';
import { IconColorDirective } from '../icon/directives/icon-color.directive';
import { IconComponent } from '../icon/icon.component';
import { AdminFilterPipe } from '../pipes/admin-filter.pipe';
import { SpinnerComponent } from '../spinner/spinner.component';
import { RtTooltip } from '../tooltip/tooltip.directive';

// TODO translations got wrong namespace app, probably should be moved into rtu namespace, so full component could be reused

@Component({
  selector: 'rt-admins-select-dialog-form',
  templateUrl: './admins-select-dialog-form.component.html',
  styleUrls: ['./admins-select-dialog-form.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AvatarComponent,
    BoxComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    CheckboxComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    DividerComponent,
    EmptyStateComponent,
    FormFieldComponent,
    GroupComponent,
    HeadingComponent,
    IconColorDirective,
    IconComponent,
    InputComponent,
    NgFor,
    NgIf,
    NgTemplateOutlet,
    ReactiveFormsModule,
    RtTooltip,
    SpinnerComponent,
    TranslatePipe,
    VirtualScrollerModule,
  ],
  providers: [AdminFilterPipe],
})
export class AdminsSelectDialogFormComponent {
  @Input() public heading: string;
  @Input() public subheading: string;
  @Input() public assignments: number[] = [];
  @Input() public admins: User[] = [];
  @Input() public disallowedAdmins: User[] = [];
  @Input() public currentAdminId: number | null;
  @Input() public loading: boolean = false;
  @Input() public loadingAdmins: boolean = false;
  @Input() public preventAdminRemove: boolean = false;
  @Input() public showCountBeforeSubheading: boolean = false;
  @Input() public minSelectedAdmins: number = 0;
  @Input() public maxSelectedAdmins: number = 0;
  @Input() public lockedAssignments: number[] = [];
  @Input() public lockedAssignmentsTooltip: string | null = null;

  @Output() public cancelClick: EventEmitter<any> = new EventEmitter();
  @Output() public submitClick: EventEmitter<any> = new EventEmitter();

  public disabledIds: Record<number, boolean> = {};
  public selectedIds = new Set<number>();
  public query = '';
  public searchControl = new FormControl<string>('', { nonNullable: true });
  public trackById = trackByProp<{ id: number }>({ prop: 'id' });
  public virtualScrollAdmins: User[] = [];
  public virtualScrollDisabledAdmins: User[] = [];

  public filteredAdmins: User[] = this.admins;
  public filteredDisallowedAdmins: User[] = this.disallowedAdmins;

  private ngOnDestroy$ = new Subject<void>();

  constructor(protected cdRef: ChangeDetectorRef, private adminFilterPipe: AdminFilterPipe) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.assignments &&
      changes.assignments.currentValue &&
      changes.assignments.firstChange
    ) {
      this.selectedIds = new Set(this.assignments);
      this.disabledIds = this.admins.reduce(
        (result, admin) => ({
          ...result,
          [admin.id]:
            this.lockedAssignments.includes(admin.id) ||
            (this.preventAdminRemove && this.currentAdminId === admin.id),
        }),
        {},
      );
    }
  }

  public ngOnInit(): void {
    const destroy$ = this.ngOnDestroy$.asObservable();
    this.searchControl.valueChanges
      .pipe(debounceTime(100), startWith(''), takeUntil(destroy$))
      .subscribe((searchControlValue: string) => {
        this.query = searchControlValue;
        this.filteredAdmins = this.adminFilterPipe.transform(this.admins, searchControlValue);
        this.filteredDisallowedAdmins = this.adminFilterPipe.transform(
          this.disallowedAdmins,
          searchControlValue,
        );
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public selectAll(): void {
    this.admins.forEach(admin => {
      this.selectedIds.add(admin.id);
      this.cdRef.detectChanges();
    });
  }

  public selectNone(): void {
    this.admins.forEach(admin => {
      if (!this.disabledIds[admin.id]) this.selectedIds.delete(admin.id);
    });
    this.cdRef.detectChanges();
  }

  public toggleAssignment(admin: User): void {
    if (this.disabledIds[admin.id]) return;

    this.selectedIds.has(admin.id)
      ? this.selectedIds.delete(admin.id)
      : this.selectedIds.add(admin.id);
    this.cdRef.detectChanges();
  }

  public getSelectedCount(): number {
    return this.selectedIds.size;
  }

  public cancelSearch(): void {
    this.searchControl.reset('');
  }

  public cancel(): void {
    this.cancelClick.emit();
  }

  public isValid(): boolean {
    if (this.preventAdminRemove) {
      const selectedAdmins = this.admins.filter(admin => this.selectedIds.has(admin.id));
      if (!selectedAdmins.map(admin => admin.id).includes(<number>this.currentAdminId)) {
        return false;
      }
    }
    if (this.minSelectedAdmins && this.getSelectedCount() < this.minSelectedAdmins) {
      return false;
    }
    if (this.maxSelectedAdmins && this.getSelectedCount() > this.maxSelectedAdmins) {
      return false;
    }

    return true;
  }

  public submit(): void {
    const selectedAdmins = this.admins.filter(admin => this.selectedIds.has(admin.id));
    this.submitClick.emit(selectedAdmins);
  }

  public onVirtualScrollUpdate(event: User[]) {
    this.virtualScrollAdmins = event;
    this.cdRef.detectChanges();
  }

  public onVirtualScrollUpdateForDisabled(event: User[]) {
    this.virtualScrollDisabledAdmins = event;
    this.cdRef.detectChanges();
  }
}
